<script setup lang="ts">
/**
 * Change-history side panel. Pure transitions in
 * `ChangeHistoryPanel.update.ts`; this shell runs Cmds (git reads, confirm,
 * emits) via `useTea`.
 */
import { fromMaybe, isJust, just, matchMaybe, type Maybe } from "../lib/maybe";
import { computed, watch } from "vue";
import { ask } from "@tauri-apps/plugin-dialog";
import {
  formatChangeWhen,
  formatRenameDetail,
  getFileChange,
  humanizeChangeTitle,
  listFileHistory,
  type FileHistoryEntry,
} from "../lib/fileHistory";
import { buildHistoryReview, changeStats, diffProse } from "../lib/proseDiff";
import { isTauri } from "../lib/runtime";
import { useTea } from "../lib/tea";
import { assertNever } from "../lib/assertNever";
import ZwButton from "./ui/ZwButton.vue";
import type { HistorySelection } from "./ChangeHistoryPanel.selection";
import {
  HISTORY_PAGE_SIZE,
  initialHistoryModel,
  makeHistoryUpdate,
  type HistoryCmd,
} from "./ChangeHistoryPanel.update";

export type { HistorySelection } from "./ChangeHistoryPanel.selection";

const props = defineProps<{
  open: boolean;
  rootPath: Maybe<string>;
  relativePath: Maybe<string>;
  /** Used in the restore confirmation dialog. */
  fileLabel: Maybe<string>;
}>();

const emit = defineEmits<{
  select: [selection: Maybe<HistorySelection>];
  restore: [content: string];
}>();

/** Never show a raw backend/git error string here. */
function friendlyHistoryError(err: unknown): string {
  console.error("[ChangeHistoryPanel] failed to load history", err);
  return "Couldn't load history for this file right now.";
}

function formatChangeSummary(added: number, removed: number): string {
  const parts: string[] = [];
  if (added > 0) {
    parts.push(added === 1 ? "1 word added" : `${added} words added`);
  }
  if (removed > 0) {
    parts.push(removed === 1 ? "1 word removed" : `${removed} words removed`);
  }
  if (parts.length === 0) return "No wording changes";
  return parts.join(" · ");
}

const update = makeHistoryUpdate(() => ({
  root: props.rootPath,
  relativePath: props.relativePath,
}));

const { model, dispatch } = useTea(initialHistoryModel, update, {
  runCmd: just(runHistoryCmd),
});

async function runHistoryCmd(cmd: HistoryCmd): Promise<void> {
  switch (cmd.kind) {
    case "loadList":
      try {
        const list = await listFileHistory(
          cmd.root,
          cmd.relativePath,
          HISTORY_PAGE_SIZE,
        );
        await dispatch({ type: "ListLoaded", entries: list });
      } catch (err) {
        await dispatch({
          type: "ListFailed",
          error: friendlyHistoryError(err),
        });
      }
      return;
    case "loadChange":
      try {
        const change = await getFileChange(cmd.root, cmd.relativePath, cmd.oid);
        const blocks = diffProse(change.before, change.after);
        const renameDetailM = formatRenameDetail(
          change.renamedFrom,
          change.renamedTo,
          change.message,
        );
        const stats = changeStats(blocks);
        const review = buildHistoryReview(change.before, change.after);
        const when = formatChangeWhen(change.time);
        await dispatch({
          type: "ChangeLoaded",
          change,
          when,
          selection: {
            change,
            title: humanizeChangeTitle(change.message),
            when,
            summary: matchMaybe(renameDetailM, {
              just: (detail) => detail,
              nothing: () => formatChangeSummary(stats.added, stats.removed),
            }),
            reviewText: review.text,
            ranges: review.ranges,
          },
        });
      } catch (err) {
        await dispatch({
          type: "ChangeFailed",
          error: friendlyHistoryError(err),
        });
      }
      return;
    case "loadMore":
      try {
        const list = await listFileHistory(
          cmd.root,
          cmd.relativePath,
          cmd.nextLimit,
        );
        await dispatch({
          type: "MoreLoaded",
          entries: list,
          requestedLimit: cmd.nextLimit,
        });
      } catch (err) {
        await dispatch({
          type: "MoreFailed",
          error: friendlyHistoryError(err),
        });
      }
      return;
    case "confirmRestore": {
      const label = matchMaybe(props.fileLabel, {
        just: (value) => value,
        nothing: () => "this file",
      });
      let confirmed = true;
      if (isTauri()) {
        confirmed = await ask(
          `Replace ${label} with the version from ${cmd.when}? Your current text stays in change history.`,
          { title: "Restore this version", kind: "warning" },
        );
      }
      if (confirmed === false) {
        await dispatch({ type: "RestoreCancelled" });
        return;
      }
      await dispatch({
        type: "RestoreConfirmed",
        content: cmd.content,
      });
      return;
    }
    case "emitSelect":
      emit("select", cmd.selection);
      return;
    case "emitRestore":
      try {
        emit("restore", cmd.content);
      } finally {
        await dispatch({ type: "RestoreFinished" });
      }
      return;
    default:
      assertNever(cmd);
  }
}

// Key on primitives: parent Maybe props are rebuilt by computeds, and a new
// `just(sameString)` must not look like a path change (that reloaded the list
// on every select, in a loop).
watch(
  [
    () => props.open,
    () => fromMaybe(props.rootPath),
    () => fromMaybe(props.relativePath),
  ] as const,
  ([open, root, rel]) => {
    if (open === false || root === null || rel === null) {
      void dispatch({ type: "ClosedOrMissingPath" });
      return;
    }
    void dispatch({ type: "OpenPaths", root, relativePath: rel });
  },
  { immediate: true },
);

const selectedChangeActive = computed(() => isJust(model.value.selectedChange));
const errorText = computed(() =>
  matchMaybe(model.value.error, {
    just: (message) => message,
    nothing: () => "",
  }),
);
const listErrorVisible = computed(
  () => isJust(model.value.error) && model.value.entries.length === 0,
);
const panelErrorVisible = computed(
  () => isJust(model.value.error) && model.value.entries.length > 0,
);

function entryRenameDetail(entry: FileHistoryEntry): Maybe<string> {
  return formatRenameDetail(entry.renamedFrom, entry.renamedTo, entry.message);
}

function entryRenameDetailText(entry: FileHistoryEntry): string {
  return matchMaybe(entryRenameDetail(entry), {
    just: (text) => text,
    nothing: () => "",
  });
}

function oidSelected(oid: string): boolean {
  return matchMaybe(model.value.selectedOid, {
    just: (selected) => selected === oid,
    nothing: () => false,
  });
}

function selectEntry(oid: string): void {
  void dispatch({ type: "SelectOid", oid });
}

function loadMore(): void {
  void dispatch({ type: "LoadMore" });
}

function onRestore(): void {
  void dispatch({ type: "RestoreRequested" });
}
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
    <div
      v-if="model.loadingList"
      class="px-3 py-4 font-ui text-[0.8rem] text-(--text-muted)"
    >
      Loading…
    </div>

    <div
      v-else-if="listErrorVisible"
      class="px-3 py-4 font-ui text-[0.8rem] text-(--danger)"
    >
      {{ errorText }}
    </div>

    <div
      v-else-if="model.entries.length === 0"
      class="px-3 py-4 font-ui text-[0.8rem] leading-relaxed text-(--text-muted)"
    >
      No saved versions yet.
    </div>

    <template v-else>
      <nav
        class="zw-scroll min-h-0 flex-1 overflow-y-auto px-1.5 py-2"
        aria-label="Saved versions"
      >
        <ol class="relative m-0 list-none p-0">
          <li
            v-for="(entry, index) in model.entries"
            :key="entry.oid"
            class="relative"
          >
            <span
              v-if="index < model.entries.length - 1"
              class="absolute top-7 bottom-0 left-[1.05rem] w-px bg-(--line)"
              aria-hidden="true"
            />
            <button
              type="button"
              class="flex w-full items-start gap-2 rounded-[0.35rem] px-1.5 py-2 text-left transition-colors"
              :class="
                oidSelected(entry.oid) ? 'bg-(--bg)' : 'hover:bg-(--hover-fill)'
              "
              :aria-current="oidSelected(entry.oid) ? 'true' : undefined"
              @click="selectEntry(entry.oid)"
            >
              <span
                class="mt-1.5 size-2 shrink-0 rounded-full border border-(--line)"
                :class="
                  oidSelected(entry.oid)
                    ? 'border-zw-amber bg-zw-amber'
                    : 'bg-(--bg-raised)'
                "
                aria-hidden="true"
              />
              <span class="min-w-0">
                <span
                  class="block font-ui text-[0.82rem] font-medium text-(--text-strong)"
                >
                  {{ humanizeChangeTitle(entry.message) }}
                </span>
                <span
                  v-if="isJust(entryRenameDetail(entry))"
                  class="mt-0.5 block truncate font-ui text-[0.72rem] text-(--text-muted)"
                  :title="entryRenameDetailText(entry)"
                >
                  {{ entryRenameDetailText(entry) }}
                </span>
                <span
                  class="mt-0.5 block font-ui text-[0.72rem] text-(--text-muted)"
                >
                  {{ formatChangeWhen(entry.time) }}
                </span>
              </span>
            </button>
          </li>
        </ol>
        <div v-if="model.hasMore" class="px-1.5 pb-1 pt-0.5">
          <ZwButton
            size="sm"
            class="w-full"
            :disabled="model.loadingMore"
            @click="loadMore"
          >
            {{ model.loadingMore ? "Loading…" : "Load more" }}
          </ZwButton>
        </div>
      </nav>

      <div
        v-if="selectedChangeActive"
        class="shrink-0 border-t border-(--line) px-3 py-2.5"
      >
        <p
          v-if="panelErrorVisible"
          class="mb-2 font-ui text-[0.72rem] text-(--danger)"
        >
          {{ errorText }}
        </p>
        <ZwButton
          size="sm"
          variant="primary"
          class="w-full"
          :disabled="model.loadingChange || model.restoring"
          @click="onRestore"
        >
          {{ model.restoring ? "Restoring…" : "Restore this version" }}
        </ZwButton>
      </div>
    </template>
  </div>
</template>
